import AxiosInstance from "@/api/axios";
import { ROLE } from "@/utils/enum";
import { getRoleFromLocalStorage } from "@/utils/helper";
import { GeneralFilterType } from "@/utils/types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

let downloadAbortController: AbortController | null = null;

interface ControlCentreFilterType extends GeneralFilterType {
  region_id?: string;
}

function downloadFile(url: string, filename?: string) {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "report";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const fetchControlCentre = createAsyncThunk(
  "controlCentre/fetchData",
  async (
    {
      organization_id,
      branch_id,
      agent,
      asset,
      date_range,
      region_id,
      reengaged,
    }: ControlCentreFilterType,
    thunkAPI,
  ) => {
    const hasValue = (val?: string | null) => Boolean(val && val.trim() !== "" && val !== "all");

    const hasBranch = hasValue(branch_id);
    const hasRegion = hasValue(region_id);

    if ((!hasBranch && !hasRegion) || branch_id === "all") {
      return null;
    }

    try {
      const response = await AxiosInstance.get(`/agents`, {
        params: {
          organization_id,
          branch_id,
          region_id,
          agents: agent,
          assets: asset,
          date_range,
          reengaged,
        },
        signal: thunkAPI.signal,
      });
      if (response.data.success) return response.data.data;
      else return null;
    } catch (error: any) {
      if (thunkAPI.signal.aborted || error.code === 'ERR_CANCELED') {
        return thunkAPI.rejectWithValue('ABORTED');
      }
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message || error?.message || 'Request failed'
      );
    }
  },
);

export const fetchReport = createAsyncThunk(
  "controlCentre/fetchReport",
  async (filters: ControlCentreFilterType, thunkAPI) => {
    try {
      const response = await AxiosInstance.post("/agents/insight-report", {
        organization_id: filters.organization_id,
        agents: filters.agent,
        sources: filters.source,
        branches: filters.branch_id,
        date_range: filters.date_range,
        reengaged: filters.reengaged,
      });

      if (response.data.success) return response.data;
      return null;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const previousFetchReport = createAsyncThunk(
  "controlCentre/previousFetchReport",
  async (filters: ControlCentreFilterType, thunkAPI) => {
    try {
      const response = await AxiosInstance.post("/agents/insight-report", {
        organization_id: filters.organization_id,
        agents: filters.agent,
        sources: filters.source,
        branches: filters.branch_id,
        date_range: filters.date_range,
        reengaged: filters.reengaged,
      });

      if (response.data.success) return response.data;
      return null;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error);
    }
  },
);

export const downloadReportCSV = createAsyncThunk(
  "controlCentre/downloadReportCSV",
  async (
    {
      organization_id,
      agent,
      source,
      branch_id,
      reengaged,
      date_range,
      metrics,
    }: any,
    thunkAPI,
  ) => {
    if (downloadAbortController) {
      downloadAbortController.abort();
    }

    const controller = new AbortController();
    downloadAbortController = controller;
    try {
      const response = await AxiosInstance.post(
        `/agents/download-overall-report`,
        {
          organization_id,
          agent,
          sources: source,
          branches: branch_id,
          reengaged,
          date_range,
          metrics,
        },
        {
          signal: controller.signal,
        }
      );

      if (response.data.success) {
        return {
          url: response?.data?.data?.url,
        };
      } else {
        return {
          url: null,
        };
      }
    } catch (error: any) {
      return null;
    } finally {
      // Only clear it if a newer download has not already taken over.
      if (downloadAbortController === controller) {
        downloadAbortController = null;
      }
    }
  },
);

interface MessageSummaryType {
  _id: string;
  message_count: number;
  handled_count: number;
  conversationRate: number;
}

interface SgFormSummaryType {
  _id: string;
  message_count: number;
}

interface TourSummaryType {
  source_group: string;
  lead_count: number;
}

interface OutOfHourVisitorSummaryType {
  agent: string;
  count: number;
}

interface LeadConversionSummaryType {
  agent: string;
  conversion: number;
}

interface VisitorSummaryType {
  _id: string;
  visitor_count: number;
}

export interface LeadSummaryType {
  _id: string;
  asset?: string;
  total_leads: number;
  leads_engaged: number;
  total_tour: number;
  total_show_count: number;
  total_sales: number;
  // leads_by_assets only: the asset split by source_group + source_name
  sources?: {
    source_group: string;
    source_name: string;
    total_leads: number;
    leads_engaged: number;
    total_tour: number;
    total_show_count: number;
  }[];
}

interface SaleSummaryType {
  _id: string;
  sale_count: number;
  conversationRate: number;
}

export interface TourDateCounts {
  booked: number;
  attended: number;
  noShow: number;
  appointments: number;
  rescheduled: number;
  cancelled: number;
}

export interface ControlCentreDataType {
  success: boolean;
  tour_by_start_date?: TourDateCounts | null;
  tour_by_created_date?: TourDateCounts | null;
  total_conversation: string;
  leads_engaged: number;
  conversionRate: string;
  outOfHourConversionRate: string;
  outOfHoursTourConversionRate: number;
  showRate: string;
  reengaged: string;
  totalRescheduled: number;
  totalLeadsWithForm: string;
  out_of_hour_conversation: number;
  leads_engaged_out_of_hour: number;
  out_of_hours_conversation_client: any[];
  out_of_hour_tour: number;
  out_of_hours_tour_client: any[];
  out_of_hour_lead: number;
  out_of_hour_visitor_branch: any[];
  message_summary: MessageSummaryType[];
  tour_summary: TourSummaryType[];
  sales_summary: MessageSummaryType[];
  agent_managed_sales_summary?: SaleSummaryType[];
  agent_assisted_sales_summary?: SaleSummaryType[];
  summary_with_sg_form: SgFormSummaryType[];
  total_visitors: number;
  visitor_summary: VisitorSummaryType[];
  out_of_hour_visitor_summary: OutOfHourVisitorSummaryType[];
  out_of_hour_conversation_summary: OutOfHourVisitorSummaryType[];
  total_leads: number;
  total_tour: number;
  total_tour_assisted?: number;
  reengaged_sent: number;
  users_reengaged: any[];
  messages_sent: any[];
  tour_booked: any[];
  lead_by_agent: LeadSummaryType[];
  leads_by_assets: LeadSummaryType[];
  leads_summary?: LeadsSummaryType;
  lead_conversion_summary: LeadConversionSummaryType[];
  out_of_hour_lead_conversion_summary: LeadConversionSummaryType[];
  out_of_hour_tour_summary: OutOfHourVisitorSummaryType[];
  trials_booked?: number;
  trials_booked_summary?: { _id: string; count: number }[];
  out_of_hour_trials_booked?: number;
  out_of_hour_trials_booked_summary?: { _id: string; count: number }[];
  trial_conversion_summary?: {
    agent: string;
    trialCount: number;
    leadCount: number;
    conversion: string;
  }[];
  out_of_hour_trial_conversion_summary?: {
    agent: string;
    trialCount: number;
    leadCount: number;
    conversion: string;
  }[];

  // leads_summery?: LeadsSummaryType;
  comparisons: comparisionsType;
  agentWiseAutomationSent?: { _id: "string"; totalCount: number }[];
  totalNoShow: number;
  totalCancelled: number;
  sales_count?: {
    agentManaged: number;
    agentAssisted: number;
  };
}

export interface ControlCentreResponseType {
  current: ControlCentreDataType | null;
  previous: ControlCentreDataType | null;
}

export interface ReportDataType {
  success: boolean;
  data: [
    {
      branchName: string;
      total_conversation: string;
      conversionRate: string;
      outOfHoursTourConversionRate: number;
      showRate: string;
      reengaged: string;
      totalRescheduled: string;
      totalLeadsWithForm: string;
      out_of_hour_conversation: number;
      out_of_hours_conversation_client: any[];
      out_of_hour_tour: number;
      out_of_hours_tour_client: any[];
      out_of_hour_lead: number;
      out_of_hour_visitor_branch: any[];
      message_summary: MessageSummaryType[];
      tour_summary: TourSummaryType[];
      sales_summary: MessageSummaryType[];
      sales_summary_report: any;
      summary_with_sg_form: SgFormSummaryType[];
      total_visitors: number;
      visitor_summary: VisitorSummaryType[];
      out_of_hour_visitor_summary: OutOfHourVisitorSummaryType[];
      out_of_hour_conversation_summary: OutOfHourVisitorSummaryType[];
      total_leads: number;
      total_tour: number;
      reengaged_sent: number;
      users_reengaged: any[];
      messages_sent: any[];
      tour_booked: any[];
      lead_by_agent: LeadSummaryType[];
      leads_summary?: LeadsSummaryType;
      lead_conversion_summary: LeadConversionSummaryType[];
      out_of_hour_lead_conversion_summary: LeadConversionSummaryType[];
      out_of_hour_tour_summary: OutOfHourVisitorSummaryType[];

      comparisons: comparisionsType;
    },
  ];
}

export interface comparisionsType {
  total_conversation_change: number;
  total_visitors_change: number;
  total_leads_change: number;
  total_tours_change: number;
  conversion_rate_change: number;
  show_rate_change: number;
  reschedule_rate_change: number;
  out_of_hour_conversation_change: number;
  out_of_hour_tour_change: number;
  leads_summary_change: {
    total_leads_change: number;
    type_summary_changes: [
      {
        type: string;
        total_count_change: number;
      },
    ];
  };
  message_summary_changes: [
    {
      _id: string;
      message_count_change: number;
    },
  ];
  visitor_summary_changes: [
    {
      _id: string;
      visitor_count_change: number;
    },
  ];
}

interface LeadsSummaryType {
  total_leads?: number;
  type_summary?: LeadsSummaryChannelType[];
  groupedLeads?: {
    crm: number;
    agent: number;
    form: number;
  };
}

interface LeadsSummaryChannelType {
  type?: string;
  total_count?: number;
  lead_summary?: LeadCountType[];
}

export interface LeadCountType {
  source_group: string;
  lead_count: number;
}

interface ControlCentreState {
  data: ControlCentreResponseType | null;
  reportData: ReportDataType | null;
  previousReportData: ReportDataType | null;
  loading: boolean;
  error: string | null;
  exportDisabled: boolean;
  url: string | null;
  // requestId of the in-flight fetchControlCentre; older ones are ignored.
  currentRequestId: string | null;
}

const initialState: ControlCentreState = {
  data: null,
  reportData: null,
  previousReportData: null,
  loading: false,
  error: null,
  exportDisabled: false,
  url: null,
  currentRequestId: null,
};

const controlCentreSliceReducer = createSlice({
  name: "controlCentre",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchControlCentre.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchControlCentre.fulfilled, (state, action) => {
        // A response from a superseded request must not overwrite newer data.
        if (state.currentRequestId !== action.meta.requestId) return;
        state.currentRequestId = null;
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchControlCentre.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.currentRequestId = null;
        state.loading = false;
        if (action.meta.aborted || action.payload === 'ABORTED') return;
        state.error = action.payload as string;
      })
      .addCase(fetchReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.loading = false;
        state.reportData = action.payload;
      })
      .addCase(fetchReport.rejected, (state, action) => {
        if (action.payload === "ABORTED") {
          state.loading = true;
          state.reportData = null;
        } else {
          state.loading = false;
          state.error = action.payload as string;
        }
      })
      .addCase(previousFetchReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(previousFetchReport.fulfilled, (state, action) => {
        state.loading = false;
        state.previousReportData = action.payload;
      })
      .addCase(previousFetchReport.rejected, (state, action) => {
        if (action.payload === "ABORTED") {
          state.loading = true;
          state.previousReportData = null;
        } else {
          state.loading = false;
          state.error = action.payload as string;
        }
      })
      .addCase(downloadReportCSV.pending, (state) => {
        state.loading = true;
        state.exportDisabled = true;
        state.error = null;
      })
      .addCase(downloadReportCSV.fulfilled, (state, action) => {
        state.loading = false;
        state.exportDisabled = false;

        state.url = action.payload?.url ?? null;

        if (action.payload?.url) {
          downloadFile(action.payload?.url);
        }
      })
      .addCase(downloadReportCSV.rejected, (state, action) => {
        state.loading = false;
        state.exportDisabled = false;
        state.error = action.payload as string;
      });
  },
});

export default controlCentreSliceReducer.reducer;
